import type { FieldState, LockReason } from "@/api/hooks";
import type { FieldProvenance } from "@/ui/ReadOnlyField";

/**
 * Mapping the contract's editorial state onto the shell's field presentation.
 *
 * The two vocabularies are not the same and were never meant to be. The
 * contract's `Provenance` is `base | overridden | adopted | added | locked`;
 * the shell's `FieldProvenance` also has `seeded`, which the contract expresses
 * through `origin.kind === "seed"` instead. This is the single place that
 * reconciles them, so #33–#36 do not each invent a different reconciliation.
 */

export function lockReasonOf(field: FieldState): LockReason | undefined {
  return field.lock.state === "locked" ? field.lock.reason : undefined;
}

export function isLocked(field: FieldState): boolean {
  return field.lock.state === "locked";
}

/**
 * `lock` wins over `provenance`.
 *
 * The two can disagree — a field could arrive `provenance: "overridden"` with
 * `lock.state: "locked"` — and when they do, the read that keeps a control
 * disabled is the safe one. ADR 0001 requires locked content to disable its
 * controls, so a disagreement resolves toward locked rather than toward
 * editable.
 */
export function fieldProvenance(field: FieldState): FieldProvenance {
  if (isLocked(field) || field.provenance === "locked") {
    return "locked";
  }

  switch (field.provenance) {
    case "added":
      return "customer-added";
    case "overridden":
      return "overridden";
    case "adopted":
      // An adopted library version replaced the base value here. The server
      // served this as `base` with an adoption origin until the effective
      // Summary counted an authored replacement as the base pack's; it is the
      // project's supply and is presented as such.
      return "adopted";
    case "base":
      return field.origin?.kind === "seed" ? "seeded" : "base";
    default:
      // An enum member added by a later contract version. Unknown editorial
      // state is not assumed to be editable.
      return "base";
  }
}

/**
 * Human-facing identity for a field, from its dotted path, WITHOUT the path.
 *
 * The contract gives no display name for a field — `FieldState` carries `path`
 * and nothing else nameable, and `ValidationFinding` carries `severity`,
 * `code`, `message` and `path`. There is no service-owned display metadata to
 * read instead, so the path is the only thing an identity can be derived from.
 * This is presentation only, and nothing matches on the derived string.
 *
 * ## Why the whole hierarchy, and not the leaf
 *
 * The first round of #488 rendered the LAST segment alone. That was safe while
 * the exact `path` was rendered beside it, and it stopped being safe the moment
 * the amendment removed the address: `summary.en.fixed_values.title` and
 * `summary.he.fixed_values.title` both became "Title", and the English and
 * Hebrew safety-screening paths both became "Safety screening". On a surface
 * that no longer renders field controls, two findings sharing a rule and a
 * message were then indistinguishable — which loses exactly the validation and
 * evidence behavior the ticket requires be preserved (Codex, #570 round 1).
 *
 * So every segment is rendered, each humanized — separators to spaces, first
 * letter raised — and joined with a chevron. Two different paths cannot
 * collapse onto one identity unless they differ only in which separator
 * character they use, which is a far weaker collision than the leaf's and is
 * not a shape the section registry produces.
 *
 * ## Humanized, never translated
 *
 * A segment is rendered as the service spelled it. `en` stays "En" rather than
 * becoming "English", because mapping a segment onto a meaning it does not
 * literally carry is the browser-side reconstruction of semantics from raw
 * paths that this ticket lists as a non-goal. The transformation is mechanical
 * and reversible by eye; it is not an interpretation.
 *
 * The result is not a dotted path and is not the machine address: it carries no
 * separator a write could use, and `FieldPatch` addresses the field by `path`,
 * which stays on the contract type and is never rendered.
 */
export function fieldIdentity(path: string): string {
  const segments = path.split(".").filter((segment) => segment.length > 0);
  if (segments.length === 0) {
    return humanizeSegment(path);
  }
  return segments.map(humanizeSegment).join(" › ");
}

function humanizeSegment(segment: string): string {
  const spaced = segment.replace(/[_-]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * The value, rendered as text.
 *
 * `FieldState.value` is `JsonValue | null`, so it can be an object or an array.
 * Rendering it as JSON is the honest option: this is a read-only projection
 * until #33–#36 build real editors, and inventing a per-type summary here would
 * be inventing a clinical presentation this ticket has no mandate for.
 */
export function fieldValueText(field: FieldState): string {
  const { value } = field;
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "string") {
    return value;
  }
  return JSON.stringify(value);
}

/** Lock reasons keyed by path, for joining findings to guard-floor locks. */
export function lockReasonsByPath(
  fields: readonly FieldState[],
): ReadonlyMap<string, LockReason> {
  const map = new Map<string, LockReason>();
  for (const field of fields) {
    const reason = lockReasonOf(field);
    if (reason) {
      map.set(field.path, reason);
    }
  }
  return map;
}
